/**
 * ReviewStep — Step 3 of the token creation wizard
 *
 * Shows full configuration summary: token details, logo preview,
 * V4 config (fee split, hook type, MEV module, extensions),
 * social links, and estimated gas cost.
 *
 * Story 2.5 Task 1
 */

import type {
	AirdropRecipient,
	CreateTokenDetailsFormData,
	SocialLinks,
	TransactionStatus,
	V4Config,
} from "@repo/shared-types";

import { useGasEstimate } from "../../hooks/chain/use-gas-estimate";
import { TransactionStateIndicator } from "../shared/TransactionStateIndicator";
import { Button } from "../ui/Button";

type ReviewStepProps = {
	details: Partial<CreateTokenDetailsFormData>;
	v4Config: Partial<V4Config>;
	logoPreviewUrl: string | null;
	transactionStatus: TransactionStatus;
	txHash?: `0x${string}` | undefined;
	txError?: string | undefined;
	blockExplorerUrl?: string | undefined;
	onBack: () => void;
	onDeploy: () => void;
	onRetry: () => void;
	airdropRecipients?: ReadonlyArray<AirdropRecipient> | undefined;
	airdropMerkleReady?: boolean | undefined;
};

function SectionTitle({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-sm font-semibold text-pastel-black uppercase tracking-wide mb-3">
			{children}
		</h3>
	);
}

function DetailRow({ label, value }: { label: string; value: string | undefined }) {
	return (
		<div className="flex justify-between py-1.5 border-b border-pastel-border/50 last:border-0">
			<span className="text-sm text-pastel-gray">{label}</span>
			<span className="text-sm font-medium text-pastel-black">{value ?? "—"}</span>
		</div>
	);
}

function extractSocialLinks(links: Partial<SocialLinks> | undefined): Record<string, string> {
	if (!links) return {};
	const result: Record<string, string> = {};
	for (const [key, val] of Object.entries(links)) {
		if (typeof val === "string" && val.length > 0) {
			result[key] = val;
		}
	}
	return result;
}

export function ReviewStep({
	details,
	v4Config,
	logoPreviewUrl,
	transactionStatus,
	txHash,
	txError,
	blockExplorerUrl,
	onBack,
	onDeploy,
	onRetry,
	airdropRecipients,
	airdropMerkleReady = true,
}: ReviewStepProps) {
	const isDeploying =
		transactionStatus === "preparing" ||
		transactionStatus === "awaiting_signature" ||
		transactionStatus === "pending";

	const gasEstimate = useGasEstimate({
		enabled: transactionStatus === "idle",
		...(v4Config.extensions ? { extensions: v4Config.extensions } : {}),
	});
	const socialLinks = extractSocialLinks(details.socialLinks);
	const hasSocialLinks = Object.keys(socialLinks).length > 0;
	const hasAirdrop = airdropRecipients && airdropRecipients.length > 0;
	const airdropExt = v4Config.extensions?.find((ext) => ext.type === "Airdrop");
	const airdropTotalTokens = hasAirdrop
		? airdropRecipients.reduce((sum, r) => sum + r.amount, 0)
		: 0;
	const canDeploy = airdropMerkleReady;

	return (
		<div className="space-y-6">
			<div className="text-center mb-6">
				<h2 className="text-2xl font-bold text-pastel-black">Review & Deploy</h2>
				<p className="text-pastel-gray mt-1">Review your token configuration before deploying</p>
			</div>

			{/* Token Details Section */}
			<div className="rounded-xl border border-pastel-border bg-white p-5">
				<SectionTitle>Token Details</SectionTitle>
				<div className="flex gap-4">
					{logoPreviewUrl && (
						<div className="shrink-0">
							<img
								src={logoPreviewUrl}
								alt={details.name ?? "Token logo"}
								className="h-16 w-16 rounded-full object-cover border border-pastel-border"
							/>
						</div>
					)}
					<div className="flex-1">
						<DetailRow label="Name" value={details.name} />
						<DetailRow label="Symbol" value={details.symbol} />
						<DetailRow label="Category" value={details.category} />
						<DetailRow label="Tagline" value={details.tagline} />
					</div>
				</div>
				{details.description && (
					<div className="mt-3">
						<p className="text-xs text-pastel-gray mb-1">Description</p>
						<p className="text-sm text-pastel-black">{details.description}</p>
					</div>
				)}
			</div>

			{/* V4 Configuration Section */}
			<div className="rounded-xl border border-pastel-border bg-white p-5">
				<SectionTitle>V4 Configuration</SectionTitle>
				<div className="mt-3">
					<p className="text-xs text-pastel-gray mb-2">Fee Split</p>
					<div className="flex gap-3">
						<div className="flex-1 rounded-lg bg-pastel-light-gray p-3 text-center">
							<p className="text-lg font-bold text-pastel-black">
								{v4Config.feeSplitCreator ?? 0}%
							</p>
							<p className="text-xs text-pastel-gray">Creator</p>
						</div>
						<div className="flex-1 rounded-lg bg-pastel-light-gray p-3 text-center">
							<p className="text-lg font-bold text-pastel-black">
								{v4Config.feeSplitTreasury ?? 0}%
							</p>
							<p className="text-xs text-pastel-gray">Treasury</p>
						</div>
						<div className="flex-1 rounded-lg bg-pastel-light-gray p-3 text-center">
							<p className="text-lg font-bold text-pastel-black">
								{v4Config.feeSplitProtocol ?? 0}%
							</p>
							<p className="text-xs text-pastel-gray">Protocol</p>
						</div>
					</div>
				</div>

				{/* Extensions */}
				{v4Config.extensions && v4Config.extensions.length > 0 && (
					<div className="mt-3">
						<p className="text-xs text-pastel-gray mb-2">Active Extensions</p>
						<div className="flex flex-wrap gap-2">
							{v4Config.extensions.map((ext) => (
								<span
									key={ext.type}
									className="rounded-full bg-pastel-vibrant-blue/10 px-3 py-1 text-xs font-medium text-pastel-vibrant-blue"
								>
									{ext.type}
								</span>
							))}
						</div>
					</div>
				)}
			</div>

			{/* Airdrop Summary Section */}
			{hasAirdrop && (
				<div className="rounded-xl border border-pastel-border bg-white p-5">
					<SectionTitle>Airdrop Summary</SectionTitle>
					{!airdropMerkleReady ? (
						<div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
							<p className="text-sm font-medium text-amber-800">Re-upload address list required</p>
							<p className="text-xs text-amber-600 mt-1">
								The Merkle tree is not in memory. Please go back to Step 2 and re-upload the address
								list.
							</p>
							<button
								type="button"
								className="mt-2 text-sm text-pastel-vibrant-blue hover:underline"
								onClick={onBack}
							>
								Go to Step 2
							</button>
						</div>
					) : (
						<>
							<DetailRow label="Recipients" value={String(airdropRecipients.length)} />
							<DetailRow label="Total Tokens" value={airdropTotalTokens.toLocaleString()} />
							{airdropExt && airdropExt.type === "Airdrop" && (
								<>
									<DetailRow
										label="Lock Period"
										value={`${String(airdropExt.lockPeriodDays)} days`}
									/>
									<DetailRow
										label="Vesting Period"
										value={`${String(airdropExt.vestingDays)} days`}
									/>
								</>
							)}
							<div className="mt-3">
								<p className="text-xs text-pastel-gray mb-2">Address Preview</p>
								<div className="space-y-1">
									{airdropRecipients.slice(0, 5).map((r) => (
										<div key={r.address} className="flex justify-between text-xs font-mono">
											<span className="text-pastel-gray truncate max-w-[200px]">{r.address}</span>
											<span className="text-pastel-black">{r.amount.toLocaleString()}</span>
										</div>
									))}
									{airdropRecipients.length > 5 && (
										<p className="text-xs text-pastel-gray">
											+ {airdropRecipients.length - 5} more
										</p>
									)}
								</div>
							</div>
						</>
					)}
				</div>
			)}

			{/* Social Links Section */}
			{hasSocialLinks && (
				<div className="rounded-xl border border-pastel-border bg-white p-5">
					<SectionTitle>Social Links</SectionTitle>
					{Object.entries(socialLinks).map(([key, value]) => (
						<DetailRow key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} value={value} />
					))}
				</div>
			)}

			{/* Gas Estimate Section */}
			<div className="rounded-xl border border-pastel-border bg-white p-5">
				<SectionTitle>Estimated Gas Cost</SectionTitle>
				{gasEstimate.isError ? (
					<div className="flex items-center gap-2 text-amber-600">
						<span>⚠️</span>
						<span className="text-sm">Unable to estimate gas</span>
					</div>
				) : (
					<>
						<p className="text-lg font-bold text-pastel-black">
							{gasEstimate.isLoading ? "Estimating..." : gasEstimate.formatted}
						</p>
						<p className="text-xs text-pastel-gray mt-1">
							Estimate may vary. Actual cost depends on network conditions.
						</p>
					</>
				)}
			</div>

			{/* Transaction State */}
			{transactionStatus !== "idle" && (
				<TransactionStateIndicator
					status={transactionStatus}
					txHash={txHash}
					error={txError}
					blockExplorerUrl={blockExplorerUrl}
					onRetry={onRetry}
				/>
			)}

			{/* Action Buttons */}
			<div className="flex justify-between pt-4">
				<Button variant="outline" onClick={onBack} disabled={isDeploying}>
					Back
				</Button>
				{transactionStatus === "idle" && (
					<Button onClick={onDeploy} disabled={!canDeploy}>
						Deploy Token
					</Button>
				)}
			</div>
		</div>
	);
}
